import React from "react";
import { NavLink } from "react-router";
import logoImage from "./assets/logo.png";

const LAST_UPDATED = "2025-12-21";

const PrivacyPolicy = () => {
  return (
    <div className="h-screen w-full bg-white text-[#0B1120] font-sans overflow-y-auto">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-3">
            <img src={logoImage} alt="neurohubs" className="h-7 w-7 object-contain" />
            <span className="text-base font-semibold tracking-tight text-blue-600">
              Neurohubs
            </span>
          </NavLink>
          <NavLink
            to="/"
            className="text-sm font-medium border border-slate-200 px-5 py-2 rounded-full hover:bg-slate-900 hover:text-white transition-all"
          >
            Back
          </NavLink>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
          Privacy Policy
        </h1>
        <p className="text-sm text-gray-500 mt-2">Last updated: {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-gray-700 leading-relaxed">
          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Overview</h2>
            <p>
              This Privacy Policy explains how Neurohubs ("we", "us") collects and
              uses information when you use our website and application.
            </p>
            <p className="text-sm text-gray-500">
              Note: The current version is a software tool intended for research and
              workflow support. It is not marketed as a certified medical device.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Information we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-medium text-[#0B1120]">Demo requests:</span> information you submit via forms (e.g., name, email, organization).
              </li>
              <li>
                <span className="font-medium text-[#0B1120]">Account and usage:</span> basic account identifiers and product usage data needed to operate the service.
              </li>
              <li>
                <span className="font-medium text-[#0B1120]">Uploaded content:</span> data you upload to the app (including qEEG-related datasets) to generate analyses and reports.
              </li>
              <li>
                <span className="font-medium text-[#0B1120]">Technical data:</span> device/browser information and logs (e.g., IP address, timestamps) to maintain security and performance.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Uploaded data and identifiers</h2>
            <p>
              You own the data you upload. You are responsible for ensuring you
              have the rights and legal basis to upload and process it.
            </p>
            <p>
              For processing, we primarily use quantitative measurement fields
              typically present in qEEG exports (for example, band-power
              amplitudes and relative power percentages) and associated
              time-related metadata (such as recording/report dates) to compute
              derived metrics and visualizations.
            </p>
            <p>
              We do not intentionally map uploaded measurements to a specific
              real-world identity. Please avoid uploading direct identifiers (such
              as patient names, national IDs, phone numbers, or addresses).
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and operate the website and application.</li>
              <li>Process demo requests and respond to inquiries.</li>
              <li>Maintain security, prevent abuse, and debug issues.</li>
              <li>Improve product usability and performance.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Data sharing</h2>
            <p>
              We may share information with service providers that help us operate
              the product (hosting, analytics, email delivery), subject to
              appropriate safeguards. We do not sell personal information.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Data retention</h2>
            <p>
              We retain information for as long as necessary to provide the service,
              comply with legal obligations, resolve disputes, and enforce
              agreements. Retention periods can vary depending on the type of data.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Your choices</h2>
            <p>
              You may request access, correction, or deletion of your personal
              information, subject to applicable law and legitimate operational
              needs.
            </p>
            <p className="text-sm text-gray-500">
              If you are located in the EEA/UK, you may have additional rights
              under applicable data protection laws (e.g., GDPR).
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Medical disclaimer</h2>
            <p>
              Neurohubs is intended to support analysis workflows. It does not
              provide medical diagnosis or treatment recommendations. Clinical
              decisions must be made by qualified professionals.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-[#0B1120]">Contact</h2>
            <p>
              For privacy-related requests, contact: {" "}
              <a
                href="mailto:office@jointhubs.com"
                className="text-blue-600 hover:text-[#0B1120] transition-colors"
              >
                office@jointhubs.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
